import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api, openWithAuth, downloadWithAuth, isTabSafeType } from './api';
import { losApi } from './losApi';
import { toast } from '../hooks/use-toast';
import { confirmDialog } from '@/components/dialogs/DialogHost';
import { Upload, FileText, Trash2, Sparkles, CheckCircle2, Loader2, AlertCircle, Download, ExternalLink, Search, User, Briefcase } from 'lucide-react';
import { Input } from '../components/ui/input';
import ContactPicker from './shared/ContactPicker';
import { extensionOf } from './shared/uploads';
import { errorMessage } from './errors';
import { useAuth } from './AuthContext';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import PageContainer from './kit/PageContainer';
import { Button } from '../components/ui/button';
import { BRAND } from './kit/tokens';
import { formatDate } from './kit/time';
import SimpleMarkdown from '../components/SimpleMarkdown';

const types = [
  { k: 'paystub', l: 'Paystub' }, { k: 'w2', l: 'W-2' }, { k: 'tax_return', l: 'Tax Return' },
  { k: 'bank_statement', l: 'Bank Statement' }, { k: 'id', l: 'ID / License' },
  { k: 'appraisal', l: 'Appraisal' }, { k: 'closing_disclosure', l: 'Closing Disclosure' },
  { k: 'loan_estimate', l: 'Loan Estimate' }, { k: 'purchase_contract', l: 'Purchase Contract' },
  { k: 'other', l: 'Other' },
];

// What the server takes on this page (backend/routers/documents_router.py).
const EXTENSIONS = ['pdf', 'jpg', 'jpeg', 'png'];
const ACCEPT = EXTENSIONS.map((e) => `.${e}`).join(',');
const MAX_BYTES = 500_000;
const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 300;
const ALL = 'all';

const loanLabel = (l) => {
  const b = l.urla?.borrower || {};
  const name = [b.first_name, b.last_name].filter(Boolean).join(' ') || 'Unnamed borrower';
  return l.property_address?.street ? `${name} · ${l.property_address.street}` : name;
};

export default function DocumentsPage() {
  const { user } = useAuth() || {};
  // The server refuses a delete without Edit deals; don't offer it (ticket 216).
  const canDelete = (user?.permissions || []).includes('loans.edit');
  const [docs, setDocs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState(ALL);
  const [uploading, setUploading] = useState(false);
  const [type, setType] = useState('paystub');
  const [contactId, setContactId] = useState(null);
  const [loanId, setLoanId] = useState(null);
  const [loans, setLoans] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const inputRef = useRef();

  const load = useCallback(async () => {
    const params = { skip: page * PAGE_SIZE, limit: PAGE_SIZE };
    if (query) params.q = query;
    if (typeFilter !== ALL) params.file_type = typeFilter;
    const { data } = await api.get('/documents', { params });
    setDocs(data.items);
    setTotal(data.total);
  }, [page, query, typeFilter]);
  useEffect(() => { load(); }, [load]);

  // Typing waits a beat, then goes back to page one with the new search.
  useEffect(() => {
    const t = setTimeout(() => { setQuery(search.trim()); setPage(0); }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [search]);

  // The loan to attach to: the picked contact's loans, or the newest ones.
  useEffect(() => {
    let live = true;
    setLoanId(null);
    losApi.listLoans(contactId ? { contact_id: contactId } : {})
      .then((r) => { if (live) setLoans(Array.isArray(r) ? r : []); })
      .catch(() => { if (live) setLoans([]); });
    return () => { live = false; };
  }, [contactId]);

  const upload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    for (const f of Array.from(files)) {
      if (!EXTENSIONS.includes(extensionOf(f.name))) {
        toast({ title: 'Upload skipped', description: `${f.name}: only PDF, JPG and PNG files` });
        continue;
      }
      if (f.size > MAX_BYTES) {
        toast({ title: 'Upload skipped', description: `${f.name}: over 500 KB. Use the loan file's Documents tab for larger files.` });
        continue;
      }
      const fd = new FormData();
      fd.append('file', f);
      fd.append('file_type', type);
      if (contactId) fd.append('contact_id', contactId);
      if (loanId) fd.append('loan_file_id', loanId);
      try {
        await api.post('/documents/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast({ title: 'Uploaded', description: f.name });
      } catch (e) { toast({ title: 'Upload failed', description: errorMessage(e, f.name) }); }
    }
    setUploading(false);
    if (page === 0) load(); else setPage(0);
  };

  // Open shows PDFs and images in a tab; anything else (old rows with no stored type too) is saved as a file.
  const view = async (d, save) => {
    setBusyId(d.document_id);
    const path = `/documents/${d.document_id}/download`;
    try {
      if (!save && isTabSafeType(d.content_type)) await openWithAuth(path, { fallbackName: d.filename });
      else await downloadWithAuth(path, d.filename);
    } finally { setBusyId(null); }
  };

  const del = async (id) => {
    if (!(await confirmDialog({ title: 'Delete document?', confirmText: 'Delete', destructive: true }))) return;
    try {
      await api.delete(`/documents/${id}`);
      if (docs.length === 1 && page > 0) setPage(page - 1); else await load();
    }
    catch { toast({ title: 'Delete failed' }); }
  };

  return (
    <PageContainer
      title="Document Center"
      subtitle="Upload borrower documents. Audrey extracts key fields automatically."
    >
      <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
        <div className="flex flex-col md:flex-row gap-4 items-start md:items-end">
          <div className="flex-1">
            <label className="text-sm font-semibold text-brand-navy">Document Type</label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>{types.map(t => <SelectItem key={t.k} value={t.k}>{t.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <input ref={inputRef} type="file" multiple hidden accept={ACCEPT} onChange={(e) => { upload(e.target.files); e.target.value = ''; }} />
          <Button onClick={() => inputRef.current?.click()} disabled={uploading} variant="accent">
            {uploading ? <><Loader2 size={16} className="animate-spin" /> Uploading...</> : <><Upload size={16} /> Upload Files</>}
          </Button>
        </div>

        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <ContactPicker
            label="Attach to a contact (optional)"
            placeholder="No contact"
            value={contactId}
            onChange={setContactId}
            testId="doc-contact"
          />
          <div>
            <label className="text-xs text-slate-500" htmlFor="doc-loan">Attach to a loan (optional)</label>
            <Select value={loanId || 'none'} onValueChange={(v) => setLoanId(v === 'none' ? null : v)}>
              <SelectTrigger id="doc-loan" className="mt-1" data-testid="doc-loan"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No loan</SelectItem>
                {loans.map((l) => <SelectItem key={l.loan_file_id} value={l.loan_file_id}>{loanLabel(l)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div onClick={() => inputRef.current?.click()}
          onDrop={(e) => { e.preventDefault(); upload(e.dataTransfer.files); }}
          onDragOver={(e) => e.preventDefault()}
          className="mt-5 border-2 border-dashed border-slate-200 rounded-xl p-10 text-center hover:border-brand-accent hover:bg-slate-50 cursor-pointer transition">
          <Upload size={28} className="mx-auto text-slate-400 mb-2" />
          <div className="font-semibold text-brand-navy">Drop files or click to upload</div>
          <div className="text-xs text-slate-500 mt-1">PDF, JPG, PNG up to 500KB — AI analysis included</div>
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by file name…" className="pl-9" aria-label="Search documents" data-testid="doc-search" />
        </div>
        <Select value={typeFilter} onValueChange={(v) => { setTypeFilter(v); setPage(0); }}>
          <SelectTrigger className="md:w-56" aria-label="Filter by type" data-testid="doc-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All types</SelectItem>
            {types.map(t => <SelectItem key={t.k} value={t.k}>{t.l}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-3">
        {docs.length === 0 && (
          <div className="text-center text-slate-400 py-16 bg-white rounded-2xl border border-slate-100">
            {query || typeFilter !== ALL ? 'No documents match.' : 'No documents uploaded yet.'}
          </div>
        )}
        {docs.map((d) => (
          <div key={d.document_id} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: BRAND.navy }}>
                <FileText size={20} style={{ color: BRAND.accentSoft }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <div className="font-semibold text-brand-navy">{d.filename}</div>
                    <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-3">
                      <span className="uppercase">{d.file_type.replace('_', ' ')}</span>
                      <span>{(d.size_bytes / 1024).toFixed(1)} KB</span>
                      <span>{formatDate(d.created_at)}</span>
                      <StatusBadge status={d.status} />
                    </div>
                    {(d.contact_name || d.loan_label) && (
                      <div className="text-xs text-slate-600 mt-1 flex flex-wrap items-center gap-3">
                        {d.contact_name && <span className="inline-flex items-center gap-1"><User size={11} /> {d.contact_name}</span>}
                        {d.loan_label && <span className="inline-flex items-center gap-1"><Briefcase size={11} /> {d.loan_label}</span>}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {busyId === d.document_id && <Loader2 size={14} className="animate-spin text-slate-400" />}
                    <button onClick={() => view(d, false)} disabled={busyId === d.document_id} className="p-2 hover:bg-slate-100 rounded-lg" aria-label={`Open ${d.filename}`} title="Open"><ExternalLink size={14} className="text-slate-600" /></button>
                    <button onClick={() => view(d, true)} disabled={busyId === d.document_id} className="p-2 hover:bg-slate-100 rounded-lg" aria-label={`Download ${d.filename}`} title="Download"><Download size={14} className="text-slate-600" /></button>
                    {canDelete && <button onClick={() => del(d.document_id)} className="p-2 hover:bg-red-50 rounded-lg" aria-label={`Delete ${d.filename}`} title="Delete"><Trash2 size={14} className="text-red-500" /></button>}
                  </div>
                </div>
                {d.ai_summary && (
                  <div className="mt-3 p-3 rounded-lg bg-slate-50 border border-slate-100">
                    <div className="flex items-center gap-2 mb-1">
                      <Sparkles size={12} style={{ color: BRAND.accent }} />
                      <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: BRAND.accent }}>Audrey Analysis</span>
                    </div>
                    <div className="text-sm text-slate-700"><SimpleMarkdown text={d.ai_summary} /></div>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between text-sm text-slate-500" data-testid="doc-pager">
          <span>Showing {page * PAGE_SIZE + 1}–{page * PAGE_SIZE + docs.length} of {total}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>Previous</Button>
            <Button variant="outline" size="sm" disabled={(page + 1) * PAGE_SIZE >= total} onClick={() => setPage((p) => p + 1)}>Next</Button>
          </div>
        </div>
      )}
    </PageContainer>
  );
}

function StatusBadge({ status }) {
  const map = {
    uploaded: { icon: FileText, color: '#94a3b8', label: 'Uploaded' },
    analyzing: { icon: Loader2, color: '#f59e0b', label: 'Analyzing' },
    analyzed: { icon: CheckCircle2, color: BRAND.accent, label: 'Analyzed' },
    error: { icon: AlertCircle, color: '#ef4444', label: 'Error' },
  };
  const m = map[status] || map.uploaded;
  const Icon = m.icon;
  return <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold" style={{ color: m.color }}><Icon size={11} className={status === 'analyzing' ? 'animate-spin' : ''} /> {m.label}</span>;
}
